import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  EyeIcon,
  EyeSlashIcon,
  BoltIcon,
  ArrowRightIcon,
  UserIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { UserRole, Workflow } from "../types";
import { apiService } from "../services/apiService";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";

interface RegisterFormData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
  companyId?: number;
}

export const Register: React.FC = () => {
  const { t } = useTranslation(["auth", "common", "errors", "users"]);
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowsLoaded, setWorkflowsLoaded] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [selectedWorkflowIds, setSelectedWorkflowIds] = useState<number[]>([]);
  const [workflowSearch, setWorkflowSearch] = useState("");
  const [workflowInstances, setWorkflowInstances] = useState<any[]>([]);
  const [workflowInstancesLoaded, setWorkflowInstancesLoaded] = useState(false);
  const [showWorkflowInstanceModal, setShowWorkflowInstanceModal] =
    useState(false);
  const [selectedWorkflowInstanceIds, setSelectedWorkflowInstanceIds] =
    useState<number[]>([]);
  const [visitorFinancialAccess, setVisitorFinancialAccess] = useState(false);
  const [workflowInstanceSearch, setWorkflowInstanceSearch] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const password = watch("password");
  const roleValue = watch("role");

  const loadWorkflows = async () => {
    try {
      setWorkflowsLoaded(false);
      const data = await apiService.getWorkflows();
      setWorkflows(data || []);
    } catch (error) {
      toast.error(t("users:noWorkflowsFound"));
    } finally {
      setWorkflowsLoaded(true);
    }
  };

  const loadWorkflowInstances = async () => {
    try {
      setWorkflowInstancesLoaded(false);
      const data = await apiService.getWorkflowInstances();
      setWorkflowInstances(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(t("users:noProjectsFound", "Proje bulunamadı"));
    } finally {
      setWorkflowInstancesLoaded(true);
    }
  };

  useEffect(() => {
    if (roleValue === UserRole.PROJECT_MANAGER && !workflowsLoaded) {
      loadWorkflows();
    }
  }, [roleValue, workflowsLoaded]);

  useEffect(() => {
    if (roleValue === UserRole.VISITOR && !workflowInstancesLoaded) {
      loadWorkflowInstances();
    }
  }, [roleValue, workflowInstancesLoaded]);

  useEffect(() => {
    if (roleValue === UserRole.PROJECT_MANAGER) {
      if (selectedWorkflowIds.length === 0) {
        setShowWorkflowModal(true);
      }
    } else if (roleValue === UserRole.VISITOR) {
      if (selectedWorkflowInstanceIds.length === 0) {
        setShowWorkflowInstanceModal(true);
      }
    } else {
      setSelectedWorkflowIds([]);
      setShowWorkflowModal(false);
      setWorkflowSearch("");
      setSelectedWorkflowInstanceIds([]);
      setShowWorkflowInstanceModal(false);
      setWorkflowInstanceSearch("");
      setVisitorFinancialAccess(false);
    }
  }, [roleValue]);

  const onSubmit = async (data: RegisterFormData) => {
    if (data.password !== data.confirmPassword) {
      toast.error(t("auth:passwordsMustMatch"));
      return;
    }

    const companyId = currentUser?.company?.id;
    if (!companyId) {
      toast.error(t("auth:companiesLoadError"));
      return;
    }

    if (data.role === UserRole.PROJECT_MANAGER && selectedWorkflowIds.length === 0) {
      toast.error(t("users:selectAtLeastOneWorkflow"));
      setShowWorkflowModal(true);
      return;
    }

    if (data.role === UserRole.VISITOR && selectedWorkflowInstanceIds.length === 0) {
      toast.error(t("users:selectAtLeastOneProject", "En az bir proje seçmelisiniz"));
      setShowWorkflowInstanceModal(true);
      return;
    }

    try {
      setLoading(true);

      // Register API call
      await apiService.register({
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        email: data.email,
        password: data.password,
        role: data.role,
        companyId,
        allowedWorkflowIds:
          data.role === UserRole.PROJECT_MANAGER ? selectedWorkflowIds : undefined,
        allowedWorkflowInstanceIds:
          data.role === UserRole.VISITOR ? selectedWorkflowInstanceIds : undefined,
        visitorFinancialAccess:
          data.role === UserRole.VISITOR ? visitorFinancialAccess : undefined,
      });

      toast.success(t("auth:registerSuccess"));
      navigate("/team-management");
    } catch (error: any) {
      toast.error(error.response?.data?.message || t("errors:createFailed"));
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkflowSelection = (workflowId: number) => {
    setSelectedWorkflowIds((prev) => {
      if (prev.includes(workflowId)) {
        return prev.filter((id) => id !== workflowId);
      }
      return [...prev, workflowId];
    });
  };

  const closeWorkflowModal = () => {
    setShowWorkflowModal(false);
    setWorkflowSearch("");
  };

  const toggleWorkflowInstanceSelection = (instanceId: number) => {
    setSelectedWorkflowInstanceIds((prev) => {
      if (prev.includes(instanceId)) {
        return prev.filter((id) => id !== instanceId);
      }
      return [...prev, instanceId];
    });
  };

  const closeWorkflowInstanceModal = () => {
    setShowWorkflowInstanceModal(false);
    setWorkflowInstanceSearch("");
  };

  const normalizedWorkflowSearch = workflowSearch.trim().toLowerCase();
  const filteredWorkflows = workflows
    .filter((workflow) => {
      if (!normalizedWorkflowSearch) return true;
      return workflow.name?.toLowerCase().includes(normalizedWorkflowSearch);
    })
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const normalizedWorkflowInstanceSearch = workflowInstanceSearch
    .trim()
    .toLowerCase();
  const filteredWorkflowInstances = workflowInstances
    .filter((instance: any) => {
      if (!normalizedWorkflowInstanceSearch) return true;
      const label =
        String(
          instance?.workflowContainerName ??
            instance?.name ??
            instance?.id ??
            ""
        ).toLowerCase() || "";
      return label.includes(normalizedWorkflowInstanceSearch);
    })
    .slice()
    .sort((a: any, b: any) =>
      String(a?.workflowContainerName ?? a?.name ?? "")
        .toLowerCase()
        .localeCompare(String(b?.workflowContainerName ?? b?.name ?? "").toLowerCase())
    );

  return (
    <div
      className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8"
      style={{ minHeight: "calc(100vh / var(--app-ui-scale, 1))" }}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-400/20 to-pink-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-emerald-400/20 to-blue-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-2xl w-full space-y-8">
        {/* Register Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <BoltIcon className="w-12 h-12 text-blue-500 mr-2" />
              <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                FlowMind
              </h1>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              {t("auth:registerTitle")}
            </h2>
            <p className="text-slate-600">{t("auth:registerSubtitle")}</p>
          </div>

          {/* Register Form */}
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Ad */}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  {t("auth:firstName")} *
                </label>
                <input
                  {...register("firstName", {
                    required: t("errors:requiredField"),
                    minLength: {
                      value: 2,
                      message: t("errors:minLength", { min: 2 }),
                    },
                  })}
                  type="text"
                  className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400"
                  placeholder={t("auth:firstName")}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              {/* Soyad */}
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  {t("auth:lastName")} *
                </label>
                <input
                  {...register("lastName", {
                    required: t("errors:requiredField"),
                    minLength: {
                      value: 2,
                      message: t("errors:minLength", { min: 2 }),
                    },
                  })}
                  type="text"
                  className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400"
                  placeholder={t("auth:lastName")}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            {/* Username */}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                {t("auth:username")} *
              </label>
              <input
                {...register("username", {
                  required: t("errors:requiredField"),
                  minLength: {
                    value: 3,
                    message: t("errors:minLength", { min: 3 }),
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_.çÇğĞıİöÖşŞüÜ]+$/,
                    message: t("auth:usernamePattern"),
                  },
                })}
                type="text"
                className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400"
                placeholder="kullanici_adi"
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.username.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                {t("auth:email")} *
              </label>
              <input
                {...register("email", {
                  required: t("errors:requiredField"),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("errors:invalidEmail"),
                  },
                })}
                type="email"
                className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400"
                placeholder="ornek@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Şirket (read-only) */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                {t("auth:company")}
              </label>
              <div className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700">
                {currentUser?.company?.name || t("auth:companiesLoadError")}
              </div>
            </div>

            {/* Rol */}
            <div>
              <label
                htmlFor="role"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                {t("auth:yourRole")} *
              </label>
              <div className="relative">
                <select
                  {...register("role", { required: t("errors:requiredField") })}
                  className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg appearance-none cursor-pointer"
                >
                  <option value="">{t("auth:selectRole")}</option>
                  <option value={UserRole.MANAGER}>
                    {t("auth:manager")}
                  </option>
                  <option value={UserRole.PROJECT_MANAGER}>
                    {t("auth:projectManager")}
                  </option>
                  <option value={UserRole.INTERN}>
                    {t("auth:intern")}
                  </option>
                  <option value={UserRole.EMPLOYEE}>
                    {t("auth:employee")}
                  </option>
                  <option value={UserRole.VISITOR}>
                    {t("users:roles.visitor", "Ziyaretçi")}
                  </option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
              {errors.role && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.role.message}
                </p>
              )}
              <div className="mt-2 p-3 bg-slate-50/80 backdrop-blur-sm rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600">
                  <span className="font-semibold text-blue-600">
                    • {t("auth:manager")}:
                  </span>{" "}
                  {t("auth:managerDescription")}
                  <br />
                  <span className="font-semibold text-indigo-600">
                    • {t("auth:projectManager")}:
                  </span>{" "}
                  {t("auth:projectManagerDescription")}
                  <br />
                  <span className="font-semibold text-purple-600">
                    • {t("auth:intern")}:
                  </span>{" "}
                  {t("auth:internDescription")}
                  <br />
                  <span className="font-semibold text-emerald-600">
                    • {t("auth:employee")}:
                  </span>{" "}
                  {t("auth:employeeDescription")}
                </p>
              </div>
            </div>

            {/* Şifre */}
            {roleValue === UserRole.PROJECT_MANAGER && (
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {t("users:projectManagerWorkflows")}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {t("users:projectManagerWorkflowsDescription")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowWorkflowModal(true)}
                    className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-700 hover:bg-white"
                  >
                    {t("common:select")}
                  </button>
                </div>
                <div className="mt-3 text-xs text-slate-600">
                  {t("common:selected")}: {selectedWorkflowIds.length}
                </div>
              </div>
            )}

            {roleValue === UserRole.VISITOR && (
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {t("users:visitorProjects", "Projeler")}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {t(
                        "users:visitorProjectsDescription",
                        "Ziyaretçinin görebileceği projeleri seçin"
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowWorkflowInstanceModal(true)}
                    className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-700 hover:bg-white"
                  >
                    {t("common:select")}
                  </button>
                </div>
                <div className="mt-3 text-xs text-slate-600">
                  {t("common:selected")}: {selectedWorkflowInstanceIds.length}
                </div>
              </div>
            )}

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                {t("auth:password")} *
              </label>
              <div className="relative">
                <input
                  {...register("password", {
                    required: t("errors:requiredField"),
                    minLength: {
                      value: 6,
                      message: t("errors:minLength", { min: 6 }),
                    },
                  })}
                  type={showPassword ? "text" : "password"}
                  className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400 pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Şifre Onay */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                {t("auth:confirmPassword")} *
              </label>
              <div className="relative">
                <input
                  {...register("confirmPassword", {
                    required: t("errors:requiredField"),
                    validate: (value) =>
                      value === password || t("errors:passwordsDoNotMatch"),
                  })}
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400 pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {/* Kayıt Ol Butonu */}
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex items-center justify-center py-4 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t("auth:registering")}
                </div>
              ) : (
                <div className="flex items-center">
                  <UserIcon className="w-5 h-5 mr-2" />
                  <span>{t("auth:registerButton")}</span>
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => navigate("/team-management")}
              className="text-sm font-semibold text-blue-600 hover:text-blue-500 transition-colors"
            >
              {t("common:back")}
            </button>
          </div>
        </div>

        {showWorkflowModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden">
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 via-blue-50 to-sky-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-white/80 border border-slate-200 flex items-center justify-center text-slate-600">
                      <Squares2X2Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        {t("users:projectManagerWorkflows")}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        {t("users:projectManagerWorkflowsDescription")}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-indigo-700 bg-indigo-100 px-2 py-1 rounded-full">
                    {t("common:selected")}: {selectedWorkflowIds.length}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={workflowSearch}
                      onChange={(e) => setWorkflowSearch(e.target.value)}
                      placeholder={t("common:searchPlaceholder")}
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedWorkflowIds(filteredWorkflows.map((w) => w.id))
                      }
                      className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                    >
                      {t("common:selectAll")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedWorkflowIds([])}
                      className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                    >
                      {t("common:clear")}
                    </button>
                  </div>
                </div>

                {!workflowsLoaded ? (
                  <div className="text-sm text-slate-500">
                    {t("common:loading")}
                  </div>
                ) : filteredWorkflows.length === 0 ? (
                  <div className="text-sm text-slate-500">
                    {t("users:noWorkflowsFound")}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[22rem] overflow-y-auto">
                    {filteredWorkflows.map((workflow) => {
                      const isSelected = selectedWorkflowIds.includes(workflow.id);
                      const displayName = workflow.name || String(workflow.id);
                      return (
                        <button
                          key={workflow.id}
                          type="button"
                          onClick={() => toggleWorkflowSelection(workflow.id)}
                          className={`flex items-center gap-3 border rounded-xl px-4 py-3 text-left transition-colors ${
                            isSelected
                              ? "border-blue-300 bg-blue-50"
                              : "border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="relative">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                              <UserCircleIcon className="h-6 w-6" />
                            </div>
                            {isSelected && (
                              <CheckCircleIcon className="h-4 w-4 text-blue-600 absolute -right-1 -bottom-1 bg-white rounded-full" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">
                              {displayName}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              #{workflow.id}
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleWorkflowSelection(workflow.id);
                            }}
                            className="h-4 w-4 text-blue-600 border-slate-300 rounded"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-200 flex justify-end gap-3 bg-white">
                <button
                  type="button"
                  onClick={closeWorkflowModal}
                  className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  {t("common:cancel")}
                </button>
                <button
                  type="button"
                  onClick={closeWorkflowModal}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t("common:save")}
                </button>
              </div>
            </div>
          </div>
        )}

        {showWorkflowInstanceModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden">
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 via-blue-50 to-sky-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-white/80 border border-slate-200 flex items-center justify-center text-slate-600">
                      <Squares2X2Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        {t("users:visitorProjects", "Projeler")}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        {t(
                          "users:visitorProjectsDescription",
                          "Ziyaretçinin görebileceği projeleri seçin"
                        )}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-slate-700 bg-slate-100 px-2 py-1 rounded-full">
                    {t("common:selected")}: {selectedWorkflowInstanceIds.length}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={workflowInstanceSearch}
                      onChange={(e) => setWorkflowInstanceSearch(e.target.value)}
                      placeholder={t("common:searchPlaceholder")}
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedWorkflowInstanceIds(
                          filteredWorkflowInstances
                            .map((i: any) => Number(i?.id))
                            .filter((id: number) => !Number.isNaN(id))
                        )
                      }
                      className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                    >
                      {t("common:selectAll")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedWorkflowInstanceIds([])}
                      className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                    >
                      {t("common:clear")}
                    </button>
                  </div>
                </div>

                {!workflowInstancesLoaded ? (
                  <div className="text-sm text-slate-500">
                    {t("common:loading")}
                  </div>
                ) : filteredWorkflowInstances.length === 0 ? (
                  <div className="text-sm text-slate-500">
                    {t("users:noProjectsFound", "Proje bulunamadı")}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[22rem] overflow-y-auto">
                    {filteredWorkflowInstances.map((instance: any) => {
                      const instanceId = Number(instance?.id);
                      const isSelected =
                        !Number.isNaN(instanceId) &&
                        selectedWorkflowInstanceIds.includes(instanceId);
                      const displayName =
                        instance?.workflowContainerName ||
                        instance?.name ||
                        String(instance?.id ?? "-");
                      return (
                        <button
                          key={String(instance?.id)}
                          type="button"
                          onClick={() => {
                            if (!Number.isNaN(instanceId)) {
                              toggleWorkflowInstanceSelection(instanceId);
                            }
                          }}
                          className={`flex items-center gap-3 border rounded-xl px-4 py-3 text-left transition-colors ${
                            isSelected
                              ? "border-blue-300 bg-blue-50"
                              : "border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="relative">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                              <UserCircleIcon className="h-6 w-6" />
                            </div>
                            {isSelected && (
                              <CheckCircleIcon className="h-4 w-4 text-blue-600 absolute -right-1 -bottom-1 bg-white rounded-full" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">
                              {displayName}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              #{String(instance?.id ?? "-")}
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            onClick={(event) => {
                              event.stopPropagation();
                              if (!Number.isNaN(instanceId)) {
                                toggleWorkflowInstanceSelection(instanceId);
                              }
                            }}
                            className="h-4 w-4 text-blue-600 border-slate-300 rounded"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}

                <label className="mt-5 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={visitorFinancialAccess}
                    onChange={(e) =>
                      setVisitorFinancialAccess(e.target.checked)
                    }
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="font-semibold text-slate-900">
                      {t(
                        "users:visitorFinancialAccess",
                        "Fatura ve teklif bilgilerini görüntüleyebilir"
                      )}
                    </span>
                    <br />
                    <span className="text-slate-500">
                      {t(
                        "users:visitorFinancialAccessDescription",
                        "İşaretlenirse ziyaretçi fatura/teklif dosyalarını ve iş tutarı-teklif miktarını görebilir."
                      )}
                    </span>
                  </span>
                </label>
              </div>

              <div className="p-6 border-t border-slate-200 flex justify-end gap-3 bg-white">
                <button
                  type="button"
                  onClick={closeWorkflowInstanceModal}
                  className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  {t("common:cancel")}
                </button>
                <button
                  type="button"
                  onClick={closeWorkflowInstanceModal}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t("common:save")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Info */}
        <div className="text-center">
          <p className="text-sm text-slate-500">
            {t("auth:copyright", { year: currentYear })}
          </p>
        </div>
      </div>
    </div>
  );
};
